/**
 * Daftar isi keranjang yang bisa diubah.
 *
 * Dipakai dua tempat: halaman /cart dan ringkasan di /checkout. Sengaja satu
 * komponen supaya kontrol jumlah dan hapus tidak pernah beda perilaku.
 *
 * `readOnly` dipakai di langkah 3 checkout — begitu pesanan terbuat di server,
 * mengubah keranjang tidak lagi mengubah apa pun dan hanya membingungkan.
 */

import { useCart } from "@/contexts/CartContext";
import { formatIDR } from "@/lib/format";

interface Props {
  readOnly?: boolean;
  /** Tampilan ringkas untuk kolom samping checkout. */
  compact?: boolean;
}

export function CartLines({ readOnly = false, compact = false }: Props) {
  const { lines, setQuantity, removeLine } = useCart();

  if (lines.length === 0) {
    return <p className="font-sans text-sm text-ink-muted">Keranjang masih kosong.</p>;
  }

  return (
    <ul className={compact ? "space-y-3" : "divide-y divide-border"}>
      {lines.map((l) => {
        // Data keranjang datang dari localStorage — jangan percaya bentuknya.
        const price = Number(l.unit_price) || 0;
        const qty = Number(l.quantity) || 1;

        return (
          <li
            key={l.variant_id}
            className={compact ? "flex gap-3" : "flex flex-wrap gap-4 py-4 items-center"}
          >
            {!compact && l.image && (
              <img
                src={l.image}
                alt=""
                className="w-16 h-16 object-cover rounded-sm border border-border shrink-0"
              />
            )}

            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm text-ink-primary truncate">
                {l.product_name}
              </p>
              <p className="font-sans text-xs text-ink-muted">
                {l.variant_name}
                {compact && ` × ${qty}`}
              </p>
              <p className="font-mono text-xs text-ink-dim mt-0.5">
                {formatIDR(price)} / item
              </p>
            </div>

            {!readOnly && !compact && (
              <div className="flex items-center border border-border rounded-sm">
                <button
                  type="button"
                  aria-label="Kurangi jumlah"
                  onClick={() => setQuantity(l.variant_id, qty - 1)}
                  className="w-8 h-8 text-ink-muted hover:text-accent"
                >
                  −
                </button>
                <span className="w-8 text-center font-mono text-sm text-ink-primary">
                  {qty}
                </span>
                <button
                  type="button"
                  aria-label="Tambah jumlah"
                  disabled={qty >= 10}
                  onClick={() => setQuantity(l.variant_id, qty + 1)}
                  className="w-8 h-8 text-ink-muted hover:text-accent disabled:opacity-40"
                >
                  +
                </button>
              </div>
            )}

            <div className={compact ? "shrink-0 text-right" : "text-right min-w-[7rem]"}>
              <p className="font-mono text-sm text-ink-primary">{formatIDR(price * qty)}</p>
              {!readOnly && !compact && (
                <button
                  type="button"
                  onClick={() => removeLine(l.variant_id)}
                  className="mt-1 font-sans text-xs text-ink-muted underline hover:text-hot"
                >
                  Hapus
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
